/**
 * How a builder conversation is named in a list.
 *
 * Shared by every surface that lists builder conversations — the navigation
 * history panel and the "Previous chats" page — because the answer to "what do
 * I call this row?" must not diverge between them. It was module-local to
 * `ConversationHistoryPanel` until a second surface needed it; extracting was
 * cheaper than letting two copies drift, which is the same reasoning behind
 * `OverviewFieldRow` (#9195).
 */

import { getEntityTypeLabel } from '@/components/csv-import/utils/entityTypeLabels';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { BuilderConversationListItem } from '@/lib/reasoning/hooks/useBuilderConversations';
import type { CrmObject } from '@/types/crm';

/**
 * Label for a builder conversation row: what is being built, not the
 * auto-generated conversation title. Prefers the resolved entity's display
 * name, falling back to the entity type label while the entity is still
 * loading or has not been proposed yet (`builder_entity_id` is null until
 * the builder's first proposal forms).
 */
export function getBuilderRowLabel(
  conv: BuilderConversationListItem,
  resolvedByEntityId: Map<string, CrmObject>
): string {
  const fallback = conv.builder_entity_type
    ? getEntityTypeLabel(conv.builder_entity_type)
    : conv.title || 'Untitled';
  if (!conv.builder_entity_id) {
    return fallback;
  }
  const object = resolvedByEntityId.get(conv.builder_entity_id);
  if (!object) {
    return fallback;
  }
  return getDisplayName(object, fallback);
}
